import React, { useEffect, useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { useFormikContext } from 'formik';
import { gfxAvatar } from 'assets';
import { Picture, Button, ErrorContainer, Loading } from 'components/UI';
import { useDispatch, useSelector } from 'react-redux';
import Actions from 'actions';
import Selectors from 'selectors';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const pixelRatio = window.devicePixelRatio || 1;
const INITIAL_CROP = {
  unit: '%',
  width: 55,
  aspect: 1,
};

const AvatarInput = (props) => {
  const { fieldClassname } = props;
  const {
    values: { avatarUrl },
    setFieldValue,
  } = useFormikContext();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [showCrop, setShowCrop] = useState(false);
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [crop, setCrop] = useState(INITIAL_CROP);
  const [completedCrop, setCompletedCrop] = useState(null);
  const dispatch = useDispatch();
  const isLoading = useSelector(
    Selectors.createLoadingSelector([
      Actions.UPLOAD_USER_IMAGE_REQUEST,
      Actions.DELETE_USER_IMAGE_REQUEST,
    ])
  );
  const onLoad = useCallback((img) => {
    imgRef.current = img;
  }, []);

  useEffect(() => {
    if (avatarUrl) setPreview(avatarUrl);
  }, [avatarUrl]);

  useEffect(() => {
    if (showCrop) document.body.classList.add('overflow-hidden');

    return () => document.body.classList.remove('overflow-hidden');
  }, [showCrop]);

  useEffect(() => {
    if (!completedCrop || !previewCanvasRef.current || !imgRef.current) return;

    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const crop = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');

    canvas.width = crop.width * pixelRatio;
    canvas.height = crop.height * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );
  }, [completedCrop]);

  const resizeCanvas = (canvas, newWidth, newHeight) => {
    const tmpCanvas = document.createElement('canvas');
    tmpCanvas.width = newWidth;
    tmpCanvas.height = newHeight;

    const ctx = tmpCanvas.getContext('2d');
    ctx.drawImage(
      canvas,
      0,
      0,
      canvas.width,
      canvas.height,
      0,
      0,
      newWidth,
      newHeight
    );

    return tmpCanvas;
  };

  const uploadAvatar = () => {
    const { current } = previewCanvasRef;
    if (!crop || !current) return;
    setError(null);
    const canvas = resizeCanvas(current, crop.width, crop.height);

    canvas.toBlob((blob) => {
      const file = new File([blob], Date.now());
      dispatch(
        Actions.uploadUserImage(file, {
          success: (response) => {
            setShowCrop(false);
            setCompletedCrop(null);
            const uploadedImage =
              response?.image_url || response?.url || URL.createObjectURL(file);
            setPreview(uploadedImage);
            setFieldValue('avatarUrl', uploadedImage);
          },
          failure: (error) =>
            setError(error?.response?.data?.error || 'Something Went Wrong'),
        })
      );
    });
  };

  const onDrop = useCallback((acceptedFiles) => {
    const objectURL = URL.createObjectURL(acceptedFiles[0]);
    setPreview(objectURL);
    setError(null);
    setCrop(INITIAL_CROP);
    setCompletedCrop(null);
    setShowCrop(true);
  }, []);

  const cancelCrop = () => {
    setShowCrop(false);
    setPreview(avatarUrl || null);
    setCrop(INITIAL_CROP);
    setCompletedCrop(null);
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: 'image/*',
    onDrop,
    noClick: true,
    noKeyboard: true,
    noDrag: true,
    multiple: false,
  });

  const RemoveButton = () => {
    previewCanvasRef.current = null;

    const removeFile = () => {
      if (showCrop) {
        setShowCrop(false);
        return setPreview(null);
      }

      setShowCrop(false);
      setPreview(null);
      setFieldValue('avatarUrl', '');
      dispatch(
        Actions.deleteUserImage({
          failure: (error) =>
            setError(error?.response?.data?.error || 'Something Went Wrong'),
        })
      );
    };

    return (
      <div
        className={classnames(
          'absolute top-0 right-0',
          'flex justify-center items-center',
          'h-4 w-4 bg-valencia rounded-full cursor-pointer',
          'text-xs text-white font-bold leading-loose'
        )}
        onClick={removeFile}
        role="presentation"
      >
        x
      </div>
    );
  };

  const DropContent = () => {
    const avatarStyle = 'rounded-full w-full h-full object-cover';

    if (isDragActive) return 'Drop the files here ...';
    if (isEmpty(preview))
      return (
        <Picture
          src={gfxAvatar}
          className={avatarStyle}
          disableParentClassName
        />
      );

    return (
      <>
        <RemoveButton />
        {showCrop ? (
          <canvas ref={previewCanvasRef} className={avatarStyle} />
        ) : (
          <img src={preview} alt="avatar" className={avatarStyle} />
        )}
      </>
    );
  };

  return (
    <div className={classnames('flex flex-col items-center', fieldClassname)}>
      {showCrop && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black-cover p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="avatar-crop-title"
        >
          <div
            className="bg-white rounded-lg shadow-card w-full max-w-140 flex flex-col overflow-hidden"
            style={{ maxHeight: 'calc(100vh - 2rem)' }}
          >
            <div className="flex-shrink-0 px-5 py-4 border-b border-alto">
              <h2 id="avatar-crop-title" className="text-lg text-ebony mb-1">
                Adjust your profile photo
              </h2>
              <p className="text-xs text-dusty">
                Drag and resize the circle to choose how your photo will appear.
              </p>
            </div>
            <div className="flex-1 min-h-0 overflow-auto bg-concrete p-4 text-center">
              <ReactCrop
                src={preview}
                crop={crop}
                onChange={setCrop}
                ruleOfThirds
                onImageLoaded={onLoad}
                onComplete={setCompletedCrop}
                setUploadSuccess
                circularCrop
                imageAlt="Crop profile photo"
                style={{ maxWidth: '100%' }}
                imageStyle={{
                  display: 'block',
                  maxHeight: 'calc(100vh - 15rem)',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </div>
            <div className="flex-shrink-0 bg-white border-t border-alto px-5 py-4">
              <ErrorContainer error={error} className="mb-3" />
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end">
                <Button
                  label="Cancel"
                  className="mt-3 sm:mt-0 sm:mr-3 text-sm"
                  theme="plain"
                  outline
                  disabled={isLoading}
                  onClick={cancelCrop}
                />
                <Button
                  label="Confirm photo"
                  className="text-sm"
                  outline
                  disabled={!completedCrop}
                  isLoading={isLoading}
                  loadInButton
                  onClick={uploadAvatar}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <div onClick={open} {...getRootProps()} role="presentation">
        <input {...getInputProps()} />
        <div className="flex relative p-1 rounded-full shadow-card h-48 w-48">
          {isLoading && (
            <Loading className="absolute top-0 right-0 rounded-full h-full w-full" />
          )}
          <DropContent />
        </div>
      </div>
      <Button
        label="Upload Photo"
        className="mx-auto text-sm my-8"
        outline
        onClick={open}
      />
      {!showCrop && <ErrorContainer error={error} className="mb-8 -mt-4" />}
    </div>
  );
};

AvatarInput.propTypes = {
  name: PropTypes.string.isRequired,
  fieldClassname: PropTypes.string,
  label: PropTypes.string,
};

AvatarInput.defaultProps = {
  fieldClassname: null,
  label: null,
};

export default AvatarInput;
